import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import WishlistToggle from "../WishlistToggle/WishlistToggle";
import { getActiveSales } from "../../../firebase/sales-functions";
import "./ProductGrid.css";

import { DEFAULT_STORE_ID } from "../../../firebase/firebase";
import { useLanguage } from "../../../context/LanguageContext";
import useCurrency from "../../../hooks/useCurrency";
import { getLocalizedProductName } from "../../../utils/multilingualUtils";
import SkeletonLoader from "../SkeletonLoader/SkeletonLoader";

const ProductGrid = ({
  products = [],
  loading = false,
  error = null,
  onProductClick,
  onAddToCart,
  formatCurrency,
  showAddToCart = false,
  showWishlist = true,
  className = "",
  gridColumns = 4,
  loadingText = "Loading products...",
  emptyStateText = "No products found",
  emptyStateAction,
  cardVariant = "default",
  showLoadMore = false,
  hasMore = false,
  onLoadMore,
  loadingMore = false,
  storeId = DEFAULT_STORE_ID,
}) => {
  const navigate = useNavigate();
  const [salesData, setSalesData] = useState({});
  const [loadingSales, setLoadingSales] = useState(false);
  const { language } = useLanguage();
  const { formatDisplay, getCurrency } = useCurrency();

  const translations = {
    en: {
      addToCart: "Add to Cart",
      outOfStock: "Out of Stock",
      loadingMore: "Loading more products...",
      loadMoreProducts: "Load More Products",
    },
    ar: {
      addToCart: "أضف إلى السلة",
      outOfStock: "نفذت الكمية",
      loadingMore: "جاري تحميل المزيد من المنتجات...",
      loadMoreProducts: "تحميل المزيد من المنتجات",
    },
  };
  const t = translations[language] || translations.en;
  const currencyFormatter = formatCurrency || formatDisplay;

  // Cached Sales Fetch
  useEffect(() => {
    const fetchSales = async () => {
      if (!storeId) return;
      setLoadingSales(true);
      try {
        const activeSales = await getActiveSales(storeId);
        const salesMap = {};
        activeSales.forEach((sale) => {
          if (!salesMap[sale.productId]) salesMap[sale.productId] = [];
          salesMap[sale.productId].push(sale);
        });
        setSalesData(salesMap);
      } catch (error) {
        console.error("ProductGrid: Error fetching sales data:", error);
      } finally {
        setLoadingSales(false);
      }
    };
    fetchSales();
  }, [storeId]);

  const calculateSavings = (sale) => {
    if (!sale) return 0;
    return (
      (parseFloat(sale.originalPrice) || 0) -
      (parseFloat(sale.discountedPrice) || 0)
    );
  };

  const getProductDisplayInfo = (product) => {
    // 1. Calculate Stock Status
    const isOutOfStock =
      product.status?.toLowerCase() === "out-of-stock" ||
      (product.variants?.length > 0
        ? product.variants.every(
            (v) => parseInt(v.quantity) !== -1 && v.quantity <= 0
          )
        : parseInt(product.quantity) !== -1 && product.quantity <= 0);

    // 2. Calculate Sales
    const sales = salesData[product.id] || [];
    let bestSale = null;

    if (sales.length > 0) {
      const productSale = sales.find((sale) => !sale.variantId);
      if (productSale) {
        bestSale = productSale;
      } else {
        const variantSales = sales.filter((sale) => sale.variantId);
        if (variantSales.length > 0) {
          bestSale = variantSales.reduce((best, current) =>
            calculateSavings(current) > calculateSavings(best) ? current : best
          );
        }
      }
    }

    if (bestSale) {
      const isVariantDiscount = !!bestSale.variantId;
      return {
        isOutOfStock, // Return computed stock status
        originalPrice: isVariantDiscount
          ? parseFloat(bestSale.originalPrice)
          : product.price,
        discountedPrice: parseFloat(bestSale.discountedPrice),
        hasDiscount: true,
        discountLabel:
          bestSale.discountType === "percentage"
            ? `${bestSale.discountValue}% OFF`
            : `${bestSale.discountValue} ${getCurrency()} OFF`,
        isVariantDiscount,
        variantInfo: isVariantDiscount ? bestSale.variantProperties : null,
        totalDiscounts: sales.length,
      };
    }

    return {
      isOutOfStock, // Return computed stock status
      originalPrice: product.price,
      discountedPrice: product.price,
      hasDiscount: false,
      discountLabel: null,
      isVariantDiscount: false,
      variantInfo: null,
      totalDiscounts: 0,
    };
  };

  // Slider Logic
  const handleProductClick = (product) => {
    if (onProductClick) onProductClick(product);
    else navigate(`/product/${product.id}`);
  };

  const handleAddToCart = (e, product, displayInfo) => {
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart({
        ...product,
        displayPrice: displayInfo.discountedPrice,
        originalPrice: displayInfo.originalPrice,
        hasDiscount: displayInfo.hasDiscount,
        discountInfo: {
          isVariantDiscount: displayInfo.isVariantDiscount,
          variantInfo: displayInfo.variantInfo,
          totalDiscounts: displayInfo.totalDiscounts,
        },
      });
    }
  };

  const getGridClass = () => {
    return `product-grid grid-columns-${gridColumns} grid-variant-${cardVariant} ${className}`.trim();
  };

  const isItemVariant = cardVariant === "items";
  const sliderRef = useRef(null);
  const displayedProducts = products;

  const scrollItems = (direction = "right") => {
    if (!sliderRef.current) return;
    const container = sliderRef.current;
    const firstCard = container.querySelector(".product-card");
    if (!firstCard) return;
    const scrollAmount = firstCard.getBoundingClientRect().width + 20;

    if (direction === "right")
      container.scrollBy({ left: scrollAmount, behavior: "smooth" });
    else container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
  };

  const handleArrowClick = (e, direction) => {
    e.stopPropagation();
    scrollItems(direction);
  };

  useEffect(() => {
    if (!isItemVariant || !sliderRef.current || !products.length) return;
    const container = sliderRef.current;
    requestAnimationFrame(() => {
      container.scrollLeft = 0;
    });
    const interval = setInterval(() => scrollItems("right"), 4000);
    return () => clearInterval(interval);
  }, [isItemVariant, products.length]);

  if (loading && products.length === 0)
    return (
      <SkeletonLoader
        count={gridColumns * 2}
        gridColumns={gridColumns}
        className={className}
      />
    );
  if (error)
    return (
      <div className="product-grid-error">
        <div className="error-icon">!</div>
        <p>{error}</p>
      </div>
    );
  if (products.length === 0)
    return (
      <div className="product-grid-empty">
        <p>{emptyStateText}</p>
      </div>
    );

  return (
    <div className="product-grid-container">
      <div
        className={`product-grid-inner ${
          isItemVariant ? "item-slider-wrapper" : ""
        }`}
      >
        {isItemVariant && (
          <button
            className="item-arrow item-arrow-left"
            onClick={(e) => handleArrowClick(e, "left")}
          >
            ‹
          </button>
        )}

        <div className={getGridClass()} ref={isItemVariant ? sliderRef : null}>
          {displayedProducts.map((product, index) => {
            // Calculate display info (including stock status) ONCE
            const displayInfo = getProductDisplayInfo(product);

            return (
              <div
                key={`${product.id}-${index}`}
                className={`product-card ${cardVariant}-card 
                  ${displayInfo.hasDiscount ? "has-discount" : ""} 
                  ${displayInfo.isVariantDiscount ? "variant-discount" : ""} 
                  ${displayInfo.isOutOfStock ? "sold-out" : ""}`}
                onClick={() => handleProductClick(product)}
              >
                <div className="product-image-container">
                  {displayInfo.hasDiscount && (
                    <span className="product-badge-sale">Sale</span>
                  )}
                  {displayInfo.isOutOfStock && (
                    <span className="product-badge-out">SOLD OUT</span>
                  )}

                  <div className="product-image-wrapper">
                    <img
                      src={
                        product.images?.[0] ||
                        product.image ||
                        "/placeholder.png"
                      }
                      alt={getLocalizedProductName(product, language)}
                      className="product-image"
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/placeholder.png";
                      }}
                    />
                    <div className="product-overlay">
                      {showAddToCart && (
                        <button
                          className="overlay-add-to-cart"
                          onClick={(e) =>
                            handleAddToCart(e, product, displayInfo)
                          }
                          disabled={displayInfo.isOutOfStock}
                        >
                          {displayInfo.isOutOfStock
                            ? t.outOfStock
                            : t.addToCart}
                        </button>
                      )}
                      {showWishlist && (
                        <div
                          className="overlay-wishlist"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <WishlistToggle product={product} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="product-details">
                  <p className="product-name">
                    {getLocalizedProductName(product, language)}
                  </p>
                  <div className="product-price-container">
                    {displayInfo.hasDiscount ? (
                      <div className="price-luxury-line">
                        <span className="discounted-priceD">
                          {currencyFormatter(
                            Number(displayInfo.discountedPrice).toFixed(2)
                          )}
                        </span>
                        <span className="original-price">
                          {currencyFormatter(
                            Number(displayInfo.originalPrice).toFixed(2)
                          )}
                        </span>
                        <span className="discount-percent">
                          {displayInfo.discountLabel.replace(" OFF", "")}
                        </span>
                      </div>
                    ) : (
                      <span className="product-price">
                        {currencyFormatter(displayInfo.originalPrice)}
                      </span>
                    )}
                    {displayInfo.hasDiscount &&
                      displayInfo.isVariantDiscount &&
                      displayInfo.variantInfo && (
                        <span className="variant-discount-info"></span>
                      )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {isItemVariant && (
          <button
            className="item-arrow item-arrow-right"
            onClick={(e) => handleArrowClick(e, "right")}
          >
            ›
          </button>
        )}
      </div>

      {loadingMore && (
        <div className="loading-more">
          <div className="loader small"></div>
          <p>{t.loadingMore}</p>
        </div>
      )}
      {showLoadMore && hasMore && !loading && !loadingMore && (
        <div className="load-more-container">
          <button className="load-more-button" onClick={onLoadMore}>
            {t.loadMoreProducts}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductGrid;
